import React, { useLayoutEffect, useRef } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { CalendarEvent } from './CalendarEvent';
import { isTimeSlotAvailable } from './calendarAvailability';
import css from './Calendar.module.css';

/** Hour shown at the top of the scroll viewport on first paint / week change */
export const CALENDAR_INITIAL_VISIBLE_HOUR = 9;

/**
 * Calendar grid component that renders the weekly schedule
 */
export const CalendarGrid = props => {
  const {
    events = [],
    availability = [],
    exceptions = [],
    currentWeek = new Date(),
    onEventClick,
  } = props;

  const scrollRef = useRef(null);
  const initialHourRowRef = useRef(null);

  // Get the start of the week (Monday)
  const getWeekStart = date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // Generate array of 7 days starting from Monday
  const generateWeekDays = weekStart => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Generate time slots for a full day (midnight–11 PM). Body scrolls so evening hours are reachable.
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour <= 23; hour++) {
      slots.push(hour);
    }
    return slots;
  };

  const formatHourLabel = hour => {
    if (hour === 0) {
      return '12 AM';
    }
    if (hour === 12) {
      return '12 PM';
    }
    return hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
  };

  // Filter events for a specific day and time
  const getEventsForSlot = (day, hour) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      const eventHour = eventDate.getHours();
      const eventDay = eventDate.getDate();
      const eventMonth = eventDate.getMonth();
      const eventYear = eventDate.getFullYear();

      return (
        eventDay === day.getDate() &&
        eventMonth === day.getMonth() &&
        eventYear === day.getFullYear() &&
        eventHour === hour
      );
    });
  };

  const weekStart = getWeekStart(currentWeek);
  const weekDays = generateWeekDays(weekStart);
  const timeSlots = generateTimeSlots();

  // Start mid-scroll with ~9 AM at the top (still scrollable to earlier/later hours)
  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const rowEl = initialHourRowRef.current;
    if (!scrollEl || !rowEl) {
      return;
    }
    const rowTop = rowEl.getBoundingClientRect().top;
    const scrollTop = scrollEl.getBoundingClientRect().top;
    scrollEl.scrollTop += rowTop - scrollTop;
  }, [currentWeek]);

  return (
    <div className={css.gridContainer}>
      {/* Header row with day names — stays visible while the body scrolls */}
      <div className={css.headerRow}>
        <div className={css.timeColumn}>
          <FormattedMessage id="Calendar.time" defaultMessage="Time" />
        </div>
        {weekDays.map((day, index) => (
          <div key={index} className={css.dayHeader}>
            <div className={css.dayName}>
              <FormattedMessage
                id={`Calendar.day.${day.getDay()}`}
                defaultMessage={day.toLocaleDateString('en-US', { weekday: 'short' })}
              />
            </div>
            <div className={css.dayDate}>{day.getDate()}</div>
          </div>
        ))}
      </div>

      {/* Scrollable time slots (mouse wheel + touch) */}
      <div
        className={css.gridBodyScroll}
        data-testid="Calendar.gridBodyScroll"
        ref={scrollRef}
      >
        <div className={css.gridBody}>
          {timeSlots.map(hour => (
            <div
              key={hour}
              className={css.timeRow}
              ref={hour === CALENDAR_INITIAL_VISIBLE_HOUR ? initialHourRowRef : undefined}
              data-hour-row={hour}
            >
              <div className={css.timeSlot}>{formatHourLabel(hour)}</div>
              {weekDays.map((day, dayIndex) => {
                const slotEvents = getEventsForSlot(day, hour);
                const isAvailable = isTimeSlotAvailable(day, hour, availability, exceptions);
                const slotClass = isAvailable ? css.availableSlot : css.unavailableSlot;

                return (
                  <div
                    key={dayIndex}
                    className={`${css.dayCell} ${slotClass}`}
                    data-testid="Calendar.slot"
                    data-available={isAvailable ? 'true' : 'false'}
                    data-hour={hour}
                  >
                    {slotEvents.map(event => (
                      <CalendarEvent
                        key={event.id}
                        event={event}
                        onClick={() => onEventClick && onEventClick(event)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
